import { Injectable, inject } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, tap, withLatestFrom, filter } from 'rxjs/operators';
import * as AppActions from './state.actions';
import { SubscriptionService } from '../api/subscription.service';
import { Store } from '@ngrx/store';
import { LoadedState, WorkflowState } from './state.state';
import {
  selectAppProfile,
  selectConversation,
  selectIsProfileActive,
  selectIsSubscriptionActive,
  selectIsWorkflowActive,
  selectProcess,
  selectUpload,
  selectWorkflow,
  selectWorkflowId,
  selectWorkflowPayload,
} from './state.selectors';
import { ProfileService } from '../api/profile.service';
import { ConversationService } from '../api/conversation.service';
import { WorkflowService } from '../api/workflow.service';
import { FileService } from '../api/file.service';
import { ProcessService } from '../api/process.service';

@Injectable()
export class WorkflowEffects {
  constructor(
    private actions$: Actions,
    private loaded: Store<{ loaded: LoadedState }>,
    private profile: Store<{ profile: WorkflowState }>,
    private conversation: Store<{ conversation: WorkflowState }>,
    private workflow: Store<{ workflow: WorkflowState }>,
    private upload: Store<{ upload: WorkflowState }>,
    private process: Store<{ process: WorkflowState }>,
  ) {}
  subscriptionService = inject(SubscriptionService);
  profileService = inject(ProfileService);
  conversationService = inject(ConversationService);
  workflowService = inject(WorkflowService);
  fileService = inject(FileService);
  processService = inject(ProcessService);

  // Subscription

  getSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getSubscription),
      withLatestFrom(this.loaded.select(selectIsSubscriptionActive)),
      filter(([action, isSubscriptionLoaded]) => !isSubscriptionLoaded),
      tap(() => {
        this.subscriptionService.getSubscription();
      }),
      map(() => AppActions.loadedSubscription()),
    ),
  );

  // Profile

  getProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getProfile),
      withLatestFrom(this.loaded.select(selectIsProfileActive)),
      filter(([action, isProfileLoaded]) => !isProfileLoaded),
      tap(() => {
        this.profileService.getProfile();
      }),
      map(() => AppActions.loadedProfile()),
    ),
  );

  saveAppProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.saveAppProfile),
      withLatestFrom(this.profile.select(selectAppProfile)),
      filter(([action, profile]) => profile === undefined),
      tap((profile) => {
        if (profile[0].appProfile) {
          this.profileService.setProfile(profile[0].appProfile);
        }
      }),
      map(() => AppActions.loadedProfile()),
    ),
  );

  // Conversation / GenAi

  askGenAi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.askGenAi),
      withLatestFrom(this.conversation.select(selectConversation)),
      filter(([action, conversation]) => conversation === undefined),
      tap((conversation) => {
        this.conversationService.askGenAi(
          conversation[0].conversation,
          conversation[0].responseKey,
          conversation[0].processKey,
        );
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );

  saveModelToWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.saveResultToWorklow),
      withLatestFrom(
        this.workflow.select(selectWorkflowPayload),
        this.workflow.select(selectWorkflowId),
      ),
      tap(([action, workflow, workflowId]) => {
        this.workflowService.saveWorkflow(workflow.workflowPayload, workflowId);
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );

  // Workflow

  createWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.createWorkflow),
      withLatestFrom(this.workflow.select(selectWorkflow)),
      tap(() => {
        this.workflowService.createWorkflow();
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );

  getWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getWorkflow),
      withLatestFrom(this.loaded.select(selectIsWorkflowActive)),
      tap((workflow) => {
        this.workflowService.getWorkflow(workflow[0].workflowId);
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );

  deleteWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteWorkflow),
      withLatestFrom(this.loaded.select(selectIsWorkflowActive)),
      tap((workflow) => {
        this.workflowService.deleteWorkflow(workflow[0].workflowId);
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );

  saveWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.saveWorkflow),
      withLatestFrom(this.workflow.select(selectWorkflowPayload)),
      tap((workflow) => {
        if (workflow[0].workflowPayload) {
          this.workflowService.saveWorkflow(
            workflow[0].workflowPayload,
            workflow[0].workflowId,
          );
        }
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );

  // Upload

  uploadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.uploadFile),
      withLatestFrom(this.upload.select(selectUpload)),
      tap((upload) => {
        this.fileService.uploadFile(upload[0].file, upload[0].workflow);
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );

  saveFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.saveFileToWorkflow),
      withLatestFrom(this.upload.select(selectUpload)),
      tap((upload) => {
        if (upload[0].workflow) {
          this.workflowService.saveWorkflow(
            {
              ...upload[0].workflow,
              promptWorkflow: {
                ...upload[0].workflow.promptWorkflow,
                uploadedFiles: [upload[0].process],
              },
            },
            upload[0].workflow.uid,
          );
        }
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );

  deleteFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteFile),
      withLatestFrom(this.upload.select(selectUpload)),
      tap((upload) => {
        this.fileService.deleteFile(upload[0].file, upload[0].workflow);
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );

  removeFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.removeFileFromWorkflow),
      withLatestFrom(this.upload.select(selectUpload)),
      tap((upload) => {
        if (upload[0].workflow) {
          this.workflowService.saveWorkflow(
            {
              ...upload[0].workflow,
              promptWorkflow: {
                ...upload[0].workflow.promptWorkflow,
                uploadedFiles:
                  upload[0].workflow.promptWorkflow.uploadedFiles.filter(
                    (uploadedFile) => uploadedFile.name !== upload[0].file.name,
                  ),
                aiResults: {
                  ...upload[0].workflow.promptWorkflow.aiResults,
                  fileSummary: '',
                },
              },
            },
            upload[0].workflow.uid,
          );
        }
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );

  // Process

  getProcessStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getProcessStatus),
      withLatestFrom(this.process.select(selectProcess)),
      tap((process) => {
        this.processService.getProcess(process[0].processId);
      }),
      map(() => AppActions.loadedWorkflow()),
    ),
  );
}
